import { useParams } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Screen } from "@/components/screen";
import { useToast } from "@/contexts";
import {
  demoteFromAdmin,
  getCompetitionMembers,
  makeMemberAdmin,
  removeCompetitionMember,
} from "@/features/competitions/api";
import type { CompetitionMember } from "@/types";

export default function CompetitionAdmin() {
  const { id } = useParams({ from: "/competition/$id/admin" }) as { id: string };
  const toast = useToast();
  const [members, setMembers] = useState<CompetitionMember[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const { data, error } = await getCompetitionMembers(id);
        if (error) toast.showAlert("Error", error.message, "error");
        if (data) setMembers(data);
      } finally {
        setLoading(false);
      }
    })();
  }, [id, toast]);

  async function handlePromote(userId: string) {
    const { error } = await makeMemberAdmin(id, userId);
    if (error) toast.showAlert("Error", error.message, "error");
    else {
      setMembers((prev) =>
        prev.map((m) => (m.userId === userId ? { ...m, role: "admin" as const } : m)),
      );
      toast.showAlert("Promoted", "Member promoted to admin", "success");
    }
  }

  async function handleDemote(userId: string) {
    const { error } = await demoteFromAdmin(id, userId);
    if (error) toast.showAlert("Error", error.message, "error");
    else {
      setMembers((prev) =>
        prev.map((m) => (m.userId === userId ? { ...m, role: "member" as const } : m)),
      );
      toast.showAlert("Demoted", "Admin demoted to member", "success");
    }
  }

  async function handleRemove(userId: string, displayName: string) {
    toast.showConfirm("Remove member", `Remove ${displayName} from this competition?`, async () => {
      const { error } = await removeCompetitionMember(id, userId);
      if (error) toast.showAlert("Error", error.message, "error");
      else {
        setMembers((prev) => prev.filter((m) => m.userId !== userId));
        toast.showAlert("Removed", `${displayName} removed from competition`, "success");
      }
    });
  }

  return (
    <Screen
      backTo="/dashboard"
      eyebrow="Admin"
      title="Manage members"
      subtitle="Promote, demote, or remove competition members."
    >
      {loading && (
        <div className="animate-pulse flex flex-col gap-2">
          {Array.from({ length: 4 }, (_, i) => i).map((n) => (
            <div
              key={n}
              className="bg-card rounded-[20px] p-4 h-16 shadow-[1px_1px_0px_0px_#1c1b2e] border border-[rgba(28,27,46,0.2)]"
            />
          ))}
        </div>
      )}

      {!loading &&
        members.map((member) => (
          <div
            key={member.userId}
            className="bg-card rounded-[20px] p-4 shadow-[1px_1px_0px_0px_#1c1b2e] border border-[rgba(28,27,46,0.2)] flex flex-col gap-3"
          >
            <div className="flex flex-row items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[rgba(255,45,126,0.1)] flex items-center justify-center shrink-0">
                <span className="text-aqua text-base font-extrabold">
                  {member.displayName?.charAt(0)?.toUpperCase() ?? "?"}
                </span>
              </div>
              <div className="flex-1 flex flex-col gap-0.5">
                <span className="text-text text-sm font-bold">{member.displayName}</span>
                <span className="text-text-dim text-xs font-bold uppercase">
                  {member.role === "owner" ? "Owner" : member.role === "admin" ? "Admin" : "Member"}
                </span>
              </div>
            </div>

            {member.role !== "owner" && (
              <div className="flex flex-row gap-2">
                {member.role === "admin" ? (
                  <button
                    type="button"
                    onClick={() => handleDemote(member.userId)}
                    className="flex-1 text-center py-2 rounded-lg bg-[rgba(0,0,0,0.04)] cursor-pointer border-0 font-inherit"
                  >
                    <span className="text-amber text-xs font-bold">Demote to member</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => handlePromote(member.userId)}
                    className="flex-1 text-center py-2 rounded-lg bg-[rgba(255,45,126,0.08)] cursor-pointer border-0 font-inherit"
                  >
                    <span className="text-aqua text-xs font-bold">Promote to admin</span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => handleRemove(member.userId, member.displayName)}
                  className="flex-1 text-center py-2 rounded-lg bg-[rgba(255,59,48,0.08)] cursor-pointer border-0 font-inherit"
                >
                  <span className="text-coral text-xs font-bold">Remove</span>
                </button>
              </div>
            )}
          </div>
        ))}
    </Screen>
  );
}
